import React from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-scroll";
import { Link as RouterLink } from "react-router-dom";
// Icons
import { FaTrophy } from "react-icons/fa";
// Components
import LazyImage from "../Elements/LazyImage";
// Assets
import HeaderImage from "../../assets/optimized/digital-transformation.webp";
import Dots from "../../assets/svg/Dots";

export default function Hero() {
  return (
    <Wrapper id="home">
      <div className="container flexSpaceCenter">
        <LeftSide className="flexCenter">
          <div>
            <HeroTitle className="extraBold font60">
              Enterprise-Grade
              <span className="gradientText"> Intelligence Solutions</span>
            </HeroTitle>
            <HeroSubtitle className="font18 semiBold">
              Africa's award-winning smart technology platform providing enterprise-grade intelligence spanning ESG analytics, compliance verification, business intelligence, and specialized industry solutions.
            </HeroSubtitle>
            <HeroDescription className="font13">
              Anton Tech empowers African businesses with data-driven decision-making through integrated intelligence solutions spanning sustainability analytics, compliance solutions, insurance innovation, business analytics, and industry-specific optimization across 8+ sectors.
            </HeroDescription>
            <ButtonGroup>
              <Link to="contact" smooth={true} duration={1000} offset={-80}>
                <PrimaryButton>
                  Get Started
                </PrimaryButton>
              </Link>
              <RouterLink to="/who-is-anton-for">
                <SecondaryButton>
                  Who is Project Anton for?
                </SecondaryButton>
              </RouterLink>
            </ButtonGroup>
            <AwardsText className="font12">
              <AwardIconWrapper><FaTrophy /></AwardIconWrapper> Most Innovative AgriTech Solutions Provider 2025 | Africa Excellence Awards 2024 Winner | Technology Innovation Award
            </AwardsText>
          </div>
        </LeftSide>
        <RightSide>
          <ImageWrapper>
            <LazyImage 
              className="radius8" 
              src={HeaderImage} 
              alt="Enterprise Intelligence & Digital Transformation"
              loading="eager"
              fetchpriority="high"
              width={1024}
              height={683}
              style={{ 
                width: '100%', 
                height: 'auto',
                maxHeight: '500px'
              }}
            />
            <FloatingCard className="flexCenter darkBg radius8">
              <div> 
                <CardTitle className="font15 whiteColor extraBold">
                  Five Core Pillars
                </CardTitle>
                <CardText className="font13 whiteColor">
                  <em>
                    ESG & Sustainability, Precision Agriculture, Compliance & Verification, and Business Intelligence—delivering enterprise-grade solutions to drive growth across African markets.
                  </em>
                </CardText>
              </div>
            </FloatingCard>
            <DotsWrapper>
              <Dots />
            </DotsWrapper>
          </ImageWrapper>
          <GreyDiv className="lightBg"></GreyDiv>
        </RightSide>
      </div>
    </Wrapper>
  );
}

const heroGradient = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

const heroFloat = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0.4;
  }
  33% {
    transform: translateY(-20px) rotate(2deg);
    opacity: 0.7;
  }
  66% {
    transform: translateY(10px) rotate(-1deg);
    opacity: 0.5;
  }
`;

const Wrapper = styled.section`
  padding-top: 100px;
  width: 100vw;
  min-height: 700px;
  position: relative;
  overflow: hidden;
  margin-left: calc(50% - 50vw);
  background: linear-gradient(-45deg, 
    rgba(7, 148, 135, 0.15), 
    rgba(27, 94, 32, 0.1), 
    rgba(7, 148, 135, 0.1), 
    rgba(25, 118, 210, 0.15),
    rgba(7, 148, 135, 0.12)
  );
  background-size: 400% 400%;
  animation: ${heroGradient} 15s ease infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 15% 85%, rgba(7, 148, 135, 0.2) 0%, transparent 40%),
      radial-gradient(circle at 85% 15%, rgba(7, 148, 135, 0.2) 0%, transparent 40%),
      radial-gradient(circle at 50% 50%, rgba(25, 118, 210, 0.1) 0%, transparent 60%),
      radial-gradient(circle at 30% 30%, rgba(102, 187, 106, 0.1) 0%, transparent 50%);
    z-index: 1;
    animation: ${heroFloat} 20s ease-in-out infinite;
    
    /* Disable animation on mobile */
    @media (min-width: 769px) {
      animation: cosmicShift 12s ease-in-out infinite;
    }
    
    @media (max-width: 768px) {
      animation: none !important;
      transform: none !important;
    }
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(30deg, rgba(7, 148, 135, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(7, 148, 135, 0.05) 87.5%, rgba(7, 148, 135, 0.05)),
      linear-gradient(150deg, rgba(7, 148, 135, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(7, 148, 135, 0.05) 87.5%, rgba(7, 148, 135, 0.05)),
      linear-gradient(30deg, rgba(25, 118, 210, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(25, 118, 210, 0.05) 87.5%, rgba(25, 118, 210, 0.05)),
      linear-gradient(150deg, rgba(102, 187, 106, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(102, 187, 106, 0.05) 87.5%, rgba(102, 187, 106, 0.05));
    background-size: 80px 80px, 80px 80px, 40px 40px, 40px 40px;
    background-position: 0 0, 0 0, 40px 40px, 40px 40px;
    z-index: 1;
    
    /* Disable animation on mobile */
    @media (min-width: 769px) {
      animation: futuristicGrid 25s linear infinite;
    }
    
    @media (max-width: 768px) {
      animation: none !important;
      transform: none !important;
    }
  }
  
  @keyframes cosmicShift {
    0%, 100% {
      transform: scale(1) rotate(0deg);
      opacity: 1;
    }
    33% {
      transform: scale(1.1) rotate(2deg);
      opacity: 0.8;
    }
    66% {
      transform: scale(0.9) rotate(-1deg);
      opacity: 0.9;
    }
  }
  
  @keyframes futuristicGrid {
    0% {
      background-position: 0 0, 0 0, 40px 40px, 40px 40px;
    }
    100% {
      background-position: 80px 80px, -80px 80px, 120px 120px, -40px 120px;
    }
  }
  
  > * {
    position: relative;
    z-index: 2;
  }
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    align-items: center;
    min-height: 600px;
  }
  
  @media (max-width: 960px) {
    margin-left: 0;
    width: 100%;
    
    .container {
      flex-direction: column;
      padding-top: 0;
      min-height: 500px;
    }
  }
`;

const LeftSide = styled.div`
  width: 50%;
  height: 100%;
  
  /* Disable animation on mobile */
  @media (min-width: 769px) {
    animation: slideInFromLeft 0.8s ease-out;
  }
  
  @media (max-width: 768px) {
    animation: none !important;
    transform: none !important;
    opacity: 1 !important;
  }
  
  @keyframes slideInFromLeft {
    from {
      opacity: 0;
      transform: translateX(-50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @media (max-width: 960px) {
    width: 100%;
    order: 2;
    margin: 50px 0;
    text-align: center;
  }
  @media (max-width: 560px) {
    margin: 40px 0 30px 0;
  }
`;

const RightSide = styled.div`
  width: 50%;
  height: 100%;
  
  /* Disable animation on mobile */
  @media (min-width: 769px) {
    animation: slideInFromRight 0.8s ease-out 0.2s both;
  }
  
  @media (max-width: 768px) {
    animation: none !important;
    transform: none !important;
    opacity: 1 !important;
  }
  
  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @media (max-width: 960px) {
    width: 100%;
    order: 1;
    margin-top: 30px;
  }
`;

const HeroTitle = styled.h1`
  line-height: 1.2;
  margin-bottom: 20px;
  
  .gradientText {
    background: linear-gradient(135deg, #079487 0%, #079487 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: 960px) {
    text-align: center;
    font-size: 48px;
  }
  
  @media (max-width: 560px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.h2`
  color: #333;
  margin-bottom: 15px;
  max-width: 500px;
  
  @media (max-width: 960px) {
    text-align: center;
    max-width: 100%;
  }
`;

const HeroDescription = styled.p`
  max-width: 520px;
  padding: 15px 0 30px 0;
  line-height: 1.6;
  color: #666;
  
  @media (max-width: 960px) {
    text-align: center;
    max-width: 100%;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 960px) {
    justify-content: center;
  }
  
  @media (max-width: 560px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #079487 0%, #079487 100%);
  color: white;
  padding: 15px 30px;
  border: none;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(7, 148, 135, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(7, 148, 135, 0.4);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #079487;
  padding: 15px 30px;
  border: 2px solid #079487;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #079487;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(7, 148, 135, 0.3);
  }
`;

const AwardIconWrapper = styled.span`
  color: #FFD700;
  margin-right: 8px;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  
  svg {
    filter: drop-shadow(0 2px 4px rgba(255, 215, 0, 0.3));
  }
`;

const AwardsText = styled.p`
  color: #666;
  font-style: italic;
  max-width: 500px;
  padding: 15px 20px;
  background: linear-gradient(135deg, rgba(7, 148, 135, 0.05) 0%, rgba(7, 148, 135, 0.05) 100%);
  border-left: 4px solid;
  border-image: linear-gradient(135deg, #079487 0%, #079487 100%) 1;
  border-radius: 0 8px 8px 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%);
    transform: translateX(-100%);
    animation: shimmer 3s infinite;
  }
  
  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
  
  @media (max-width: 960px) {
    text-align: center;
    max-width: 100%;
    margin: 0 auto;
  }
`;

const GreyDiv = styled.div`
  width: 30%;
  height: 500px;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 0;
  @media (max-width: 960px) {
    display: none;
  }
`;

const ImageWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  position: relative;
  z-index: 9;
  max-width: 500px;
  @media (max-width: 960px) {
    width: 100%;
    justify-content: center;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const FloatingCard = styled.div`
  position: absolute;
  left: 0;
  bottom: 50px;
  max-width: 350px;
  padding: 25px;
  z-index: 99;
  backdrop-filter: blur(10px);
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 960px) {
    left: 20px;
  }
  @media (max-width: 560px) {
    bottom: -50px;
  }
`;

const CardTitle = styled.h3`
  margin-bottom: 10px;
`;

const CardText = styled.p`
  line-height: 1.5;
`;

const DotsWrapper = styled.div`
  position: absolute;
  right: -100px;
  bottom: 100px;
  z-index: 2;
  @media (max-width: 960px) {
    right: 100px;
  }
  @media (max-width: 560px) {
    display: none;
  }
`;
